/**
 * Inline SVG brand mark for UID Infotech.
 * - Hexagonal "shield" silhouette (trust / enterprise / IT)
 * - Cyan→blue diagonal gradient matching the dark navy theme
 * - Stylized "U" monogram inside
 * - Emerald accent dot evoking the live-status pulse from the video CTA
 */
export function LogoMark({ size = 36, className = "", animatePulse = false }) {
  const gradId = `uid-grad-${size}`;
  const innerId = `uid-inner-${size}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="UID Infotech"
    >
      <defs>
        <linearGradient id={gradId} x1="6" y1="3" x2="42" y2="45" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#22D3EE" />
          <stop offset="55%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#1D4ED8" />
        </linearGradient>
        <linearGradient id={innerId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
      </defs>

      {/* Hexagonal shield */}
      <path
        d="M24 2.5 L42.5 13 L42.5 35 L24 45.5 L5.5 35 L5.5 13 Z"
        fill={`url(#${gradId})`}
      />
      {/* Subtle inner highlight stroke for depth */}
      <path
        d="M24 2.5 L42.5 13 L42.5 35 L24 45.5 L5.5 35 L5.5 13 Z"
        stroke={`url(#${innerId})`}
        strokeWidth="1.2"
        fill="none"
      />

      {/* Stylized U-monogram (brackets converging into a smile curve) */}
      <path
        d="M15.5 16 V27.5 Q15.5 33.5 24 33.5 Q32.5 33.5 32.5 27.5 V16"
        stroke="#0A101D"
        strokeWidth="3.2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Inner accent dot — represents the always-on live status */}
      <circle cx="24" cy="12.5" r="2" fill="#10B981">
        {animatePulse && (
          <animate attributeName="opacity" values="1;0.35;1" dur="2s" repeatCount="indefinite" />
        )}
      </circle>
    </svg>
  );
}

/**
 * Full brand lockup: icon + wordmark.
 * Variants:
 *  - default (white wordmark, used on dark backgrounds)
 *  - compact: hides the "Infotech" suffix in tight spaces
 */
export default function Logo({
  size = 34,
  className = "",
  wordmarkClassName = "",
  compact = false,
  animatePulse = false,
  ariaLabel = "UID Infotech",
}) {
  return (
    <span
      className={`inline-flex items-center gap-2.5 ${className}`}
      aria-label={ariaLabel}
    >
      <LogoMark size={size} animatePulse={animatePulse} />
      <span className={`font-heading font-extrabold tracking-tight leading-none ${wordmarkClassName}`}>
        <span className="text-white">UID</span>
        {!compact && (
          <>
            <span className="text-cyan-300/90">·</span>
            <span className="text-blue-400">Infotech</span>
          </>
        )}
      </span>
    </span>
  );
}
